/**
 * As escalas e os números do visual do CPTu (onda 313; spec 07 §6 e §3.2) — contas puras, sem DOM.
 *
 * O desenho é em pixel (a largura medida pelo `useLargura`, nunca esticada por `viewBox`): a escala leva
 * o valor ao pixel e o pixel de volta ao valor. Linear ou log; no log, valor ≤ 0 não tem lugar no eixo
 * e é NaN — nunca Infinity, como no motor («não se aplica» é NaN).
 *
 * Os números saem no padrão brasileiro (1.234,56) com o menos tipográfico (U+2212, o que alinha em
 * coluna, como o `sinal()` da bancada) e o travessão no lugar do que não existe. A notação científica
 * do k e do cₕ sai com três algarismos significativos, como o CPeT-IT exporta (3,07×10⁻⁶).
 */

/** O tipo de um eixo. */
export type EscalaDoEixo = 'linear' | 'log';

/** Uma escala: o valor ao pixel (`para`) e o pixel ao valor (`de`). */
export interface Escala {
  para(v: number): number;
  de(px: number): number;
}

/** O número, ou NaN quando ele é infinito ou não existe. */
const finito = (x: number): number => (Number.isFinite(x) ? x : NaN);

/**
 * A escala de um eixo: o domínio [d0, d1] vai à faixa [f0, f1] (em pixel; f0 > f1 inverte o sentido, como
 * no eixo vertical do ábaco, em que o valor cresce para cima). No log, valor ≤ 0 é NaN, e um domínio com
 * ponta ≤ 0 não tem escala (tudo NaN). Domínio de largura zero: o valor vai ao meio da faixa.
 */
export function escala(tipo: EscalaDoEixo, dominio: [number, number], faixa: [number, number]): Escala {
  const [f0, f1] = faixa;
  if (tipo === 'log') {
    const [d0, d1] = dominio;
    if (!(d0 > 0 && d1 > 0) || !Number.isFinite(d0) || !Number.isFinite(d1)) {
      return { para: () => NaN, de: () => NaN };
    }
    const l0 = Math.log10(d0);
    const l1 = Math.log10(d1);
    if (l1 === l0) return { para: (v) => (v > 0 && Number.isFinite(v) ? (f0 + f1) / 2 : NaN), de: () => d0 };
    const k = (f1 - f0) / (l1 - l0);
    return {
      para: (v) => (v > 0 && Number.isFinite(v) ? finito(f0 + (Math.log10(v) - l0) * k) : NaN),
      de: (px) => finito(10 ** (l0 + (px - f0) / k)),
    };
  }
  const [d0, d1] = dominio;
  if (d1 === d0) return { para: (v) => (Number.isFinite(v) ? (f0 + f1) / 2 : NaN), de: () => d0 };
  const k = (f1 - f0) / (d1 - d0);
  return {
    para: (v) => finito(f0 + (v - d0) * k),
    de: (px) => finito(d0 + (px - f0) / k),
  };
}

/** O passo «redondo» (1, 2 ou 5 × 10ⁿ) mais perto de `bruto`. */
function passoRedondo(bruto: number): number {
  const potencia = 10 ** Math.floor(Math.log10(bruto));
  const fracao = bruto / potencia;
  const base = fracao < 1.5 ? 1 : fracao < 3.5 ? 2 : fracao < 7.5 ? 5 : 10;
  return base * potencia;
}

/** Tira o ruído do ponto flutuante de um múltiplo do passo (0,30000000000000004 → 0,3). */
const limpo = (v: number, passo: number): number => {
  const casas = Math.max(0, -Math.floor(Math.log10(passo)) + 1);
  const r = Number(v.toFixed(Math.min(casas, 12)));
  return r === 0 ? 0 : r;
};

/**
 * As marcas de um eixo. No log, as décadas (1, 10, 100 …) dentro do domínio — e, quando o domínio tem
 * menos de duas, também os 2 e 5 de cada década. No linear, uns `alvo` (padrão 5) passos redondos de 1, 2
 * ou 5 × 10ⁿ, a partir do primeiro múltiplo do passo dentro do domínio.
 */
export function marcas(tipo: EscalaDoEixo, dominio: [number, number], alvo = 5): number[] {
  const lo = Math.min(dominio[0], dominio[1]);
  const hi = Math.max(dominio[0], dominio[1]);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [];
  if (tipo === 'log') {
    if (!(lo > 0)) return [];
    const folga = 1e-9;
    const de = Math.ceil(Math.log10(lo) - folga);
    const ate = Math.floor(Math.log10(hi) + folga);
    const saida: number[] = [];
    if (ate - de >= 1) {
      for (let e = de; e <= ate; e++) saida.push(Number((10 ** e).toPrecision(12)));
      return saida;
    }
    for (let e = de - 1; e <= ate; e++) {
      for (const m of [1, 2, 5]) {
        const v = Number((m * 10 ** e).toPrecision(12));
        if (v >= lo * (1 - folga) && v <= hi * (1 + folga)) saida.push(v);
      }
    }
    return saida;
  }
  if (hi === lo) return [lo];
  const passo = passoRedondo((hi - lo) / Math.max(1, alvo));
  const saida: number[] = [];
  const primeiro = Math.ceil(lo / passo - 1e-9);
  for (let k = primeiro; k * passo <= hi + passo * 1e-9; k++) saida.push(limpo(k * passo, passo));
  return saida;
}

/** √10: meia década. */
const MEIA_DECADA = Math.sqrt(10);

/**
 * O domínio de um conjunto de séries: o menor e o maior valor, sem os NaN (e, no log, sem os ≤ 0). Com
 * `comZero`, o 0 sempre dentro (o Bq com os negativos, o U₂). Sem valor nenhum, [0, 1] no linear e
 * [1, 10] no log; um valor só, ±1 em volta dele (no log, meia década para cada lado).
 */
export function dominioDe(
  series: readonly ArrayLike<number>[],
  tipo: EscalaDoEixo,
  x?: { comZero?: boolean },
): [number, number] {
  let lo = Infinity;
  let hi = -Infinity;
  for (const s of series) {
    for (let i = 0; i < s.length; i++) {
      const v = s[i];
      if (!Number.isFinite(v) || (tipo === 'log' && !(v > 0))) continue;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
  }
  if (x?.comZero && tipo === 'linear') {
    lo = Math.min(lo, 0);
    hi = Math.max(hi, 0);
  }
  if (lo === Infinity) return tipo === 'log' ? [1, 10] : [0, 1];
  if (lo === hi) return tipo === 'log' ? [lo / MEIA_DECADA, lo * MEIA_DECADA] : [lo - 1, hi + 1];
  return [lo, hi];
}

/** O menos tipográfico (U+2212), que alinha em coluna de números. */
const MENOS = '−';

/** O que não existe, na tela. */
export const SEM_VALOR = '—';

/** 1234567 → «1.234.567»: o inteiro (sem sinal) com o ponto dos milhares. */
const milhares = (inteiro: string): string => inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

/**
 * O número no padrão brasileiro, com `casas` casas: «1.234,56», «−43,90»; o que arredonda para zero sai
 * sem sinal («0,00»); NaN e infinito são «—».
 */
export function numeroBr(v: number, casas: number): string {
  if (!Number.isFinite(v)) return SEM_VALOR;
  const c = Math.max(0, Math.min(12, Math.trunc(casas)));
  const texto = Math.abs(v).toFixed(c);
  const [inteiro, decimal] = texto.split('.');
  const zero = /^[0.]+$/.test(texto);
  const corpo = decimal === undefined ? milhares(inteiro) : `${milhares(inteiro)},${decimal}`;
  return v < 0 && !zero ? `${MENOS}${corpo}` : corpo;
}

const SOBRESCRITOS: Readonly<Record<string, string>> = {
  0: '⁰', 1: '¹', 2: '²', 3: '³', 4: '⁴', 5: '⁵', 6: '⁶', 7: '⁷', 8: '⁸', 9: '⁹', '-': '⁻',
};

/**
 * «1,00 – 1,45» — a faixa de profundidades como a casa e o documento a escrevem (`faixaProfundidade` do
 * `formato.ts`, P-FMT-05: duas casas e o « – » espaçado), aqui sem o `formato.ts` (o visual não sai da pasta).
 * A unidade fica com quem chama: «11,10 – 11,20 m».
 */
export function faixaBr(de: number, ate: number): string {
  return `${numeroBr(de, 2)} – ${numeroBr(ate, 2)}`;
}

/**
 * Notação científica no padrão brasileiro, com `algarismos` significativos (padrão 3, os do CPeT-IT no
 * k e no cₕ), com os espaços da casa e do laudo (o `cientifica` do `formato.ts`, P-FMT-01): 3,0684×10⁻⁶ →
 * «3,07 × 10⁻⁶»; a mantissa que arredonda para 10 sobe a potência (9,996×10⁻⁹ → «1,00 × 10⁻⁸»). Zero é «0»;
 * NaN e infinito, «—».
 */
export function cientificaBr(v: number, algarismos = 3): string {
  if (!Number.isFinite(v)) return SEM_VALOR;
  if (v === 0) return '0';
  const a = Math.max(1, Math.min(15, Math.trunc(algarismos)));
  const [mantissa, expoente] = Math.abs(v).toExponential(a - 1).split('e');
  const potencia = String(Number(expoente)).replace(/[-0-9]/g, (c) => SOBRESCRITOS[c]);
  const corpo = `${mantissa.replace('.', ',')} × 10${potencia}`;
  return v < 0 ? `${MENOS}${corpo}` : corpo;
}
